import { useMemo } from 'react';

import useLibrarySectionsQuery from '@/hooks/queries/useLibrarySectionsQuery';

/**
 * Resolves a category slug to its CMS section title.
 *
 * The titles are authored and localized; a slug is not a label. Prettifying
 * the slug instead gets 16 of 26 categories wrong — `ai-social-virals` is
 * "TrendFX", `3d-characters` is "3D Characters", `generate-with-lyria` keeps
 * its lowercase "with".
 *
 * Reads the app-wide `['library-sections']` cache, so this costs no request:
 * the sections are already loaded for the home rows and the preset feed.
 * Returns `null` for a slug the hub doesn't have — callers should omit the
 * label rather than fall back to a guess.
 */
const useCategoryLabel = () => {
  const { data: sections } = useLibrarySectionsQuery();

  return useMemo(() => {
    const titleBySlug = new Map(
      (sections ?? []).map((section) => [section.slug, section.title])
    );

    return (slug: string | null): string | null =>
      (slug && titleBySlug.get(slug)) || null;
  }, [sections]);
};

export default useCategoryLabel;
